import React from "react";
import { FormattedNumber, useIntl } from "react-intl";
import { scaleLinear } from "d3-scale";
import { styled } from "@mui/material";

import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardMedia from "@mui/material/CardMedia";
import CardHeader from "@mui/material/CardHeader";
import CardContent from "@mui/material/CardContent";
import CardActionArea from "@mui/material/CardActionArea";
import Stack from "@mui/material/Stack";

import ThumbUpIcon from "@mui/icons-material/ThumbUp";

import { TVehicle } from "types";

import { Chip, TChip, UChip, DChip } from "../components";
import { Typography } from "@mui/material";
import { isNeuwagen, getEnergyType, checkQuality } from "components/EnVKV";
import { FinanceTypesIndicator } from "../components/FinanceTypesIndicator";

interface ICard {
  vehicle: TVehicle;
  onClickHandler?: any;
  paperProps?: any;
  debug?: boolean;
  vatExcluded?: boolean;
}

const defaultPicture = "https://cdn.carbando.eu/develop/logo512.png";

const Component: React.FC<ICard> = ({
  vehicle,
  paperProps = {},
  onClickHandler = () => null,
  debug = false,
  vatExcluded = false,
}): JSX.Element => {
  const { formatMessage: t } = useIntl();

  const picture =
    vehicle.picturesCount > 0
      ? vehicle.picturesPath + vehicle.wgnr + "_1.jpg"
      : null;
  const {
    kraftstoff,
    erstzulassung,
    mwst_ausweisbar,
    getriebe,
    kw,
    km,
    app360_bilder_count,
    fzart,
    locations,
  } = vehicle;

  const className = getVehicleClassName(vehicle);
  const title = `${vehicle.modell.marke.name} - ${vehicle.modell.name}`;
  const titleLong = `${vehicle.modell.marke.name} - ${vehicle.modell.name} - ${vehicle.version}`;
  const carIsNew = isNeuwagen(vehicle);
  const energyType = getEnergyType(vehicle);
  const quality = checkQuality(vehicle);

  const EnVKVText = t(
    {
      id: "envkv." + energyType + ".message",
      defaultMessage: "EnVKV Daten",
    },
    { ...vehicle },
  );

  const TextWrapper = styled("div")({
    display: "flex",
    flexDirection: "column",
  });
  const vatFactor = vatExcluded ? 1.19 : 1;
  const preisTitle = vatExcluded
    ? "netto zuzüglich MwSt., nur für Gewerbekunden"
    : "inkl. MwSt.";
  return (
    <Card
      className={"modell " + className}
      square
      sx={{ boxShadow: 5, position: "relative" }}
    >
      {debug && (
        <QualityIcon
          score={quality.score}
          sx={{ position: "absolute", top: 2, right: 2, zIndex: 1 }}
          title={JSON.stringify(quality)}
        />
      )}
      <CardActionArea
        onClick={onClickHandler}
        disableRipple
        sx={{
          height: "100%",
          display: "grid",
          justifyContent: "space-between",
        }}
      >
        <Stack
          sx={{ justifyContent: "space-between", height: "100%" }}
          direction="column"
        >
          <Box sx={{ position: "relative" }}>
            <CardMedia
              component="img"
              loading="lazy"
              image={picture || defaultPicture}
              sx={{
                objectPosition: "top center",
                aspectRatio: "12/9",
              }}
              {...{
                title: titleLong,
                alt: t(
                  {
                    id: "pages.modell.card.image.alt",
                    defaultMessage: "Foto der Angebotsnummer {wgnr}",
                  },
                  {
                    wgnr: vehicle.wgnr,
                  },
                ),
              }}
            />
            <div className="image-overlay" />
          </Box>
          <CardHeader
            {...{ title, alt: titleLong }}
            subheader={vehicle.version}
            titleTypographyProps={{
              variant: "h5",
              color: "primary",
              component: "h2",
            }}
            subheaderTypographyProps={{
              variant: "body2",
              color: "primary",
              sx: { textOverflow: "ellipsis" },
            }}
          />

          <Stack
            sx={{ justifyContent: "space-between", height: "100%" }}
            direction="column"
          >
            <CardContent sx={{ py: 0 }}>
              <Stack
                direction="row"
                spacing={1}
                sx={{ fontWeight: "bold", flexWrap: "wrap" }}
                useFlexGap
              >
                <TChip
                  parameter="fzart"
                  value={fzart.id}
                  defaultMessage={fzart.name}
                />
                {fzart.id !== 1 && <UChip value={km} unit="kilometer" />}
                {fzart.id !== 1 && <DChip value={erstzulassung} prefix="EZ " />}
                <TChip
                  parameter="kraftstoff"
                  value={kraftstoff.id}
                  defaultMessage={kraftstoff.name}
                />
                <TChip
                  parameter="getriebe"
                  value={getriebe?.id}
                  defaultMessage={getriebe?.name}
                />
                <Chip
                  label={
                    kw ? `${kw} kW (${Math.round(kw * 1.35962)} PS)` : null
                  }
                />
                <Chip label={vehicle.metallic ? "metallic" : null} />
                {fzart.id !== 1 && (
                  <Chip
                    label={
                      vehicle.hasOwnProperty("unfallwagen") &&
                      vehicle.unfallwagen === false
                        ? "unfallfrei"
                        : null
                    }
                  />
                )}
                {locations && locations.length > 1 && (
                  <Chip label={locations.length + " Standorte"} />
                )}
                <Chip
                  label={
                    mwst_ausweisbar
                      ? t({
                          id: "common.mwst_ausweisbar.label",
                          defaultMessage: "MwSt. ausweisbar",
                        })
                      : null
                  }
                />
                <Chip
                  sx={{ backgroundColor: "primary.dark", color: "white" }}
                  label={app360_bilder_count ? "360°-Ansicht" : null}
                />
                <FinanceTypesIndicator vehicle={vehicle} translate={t} />
              </Stack>
            </CardContent>

            {carIsNew && (
              <CardContent
                sx={{
                  display: "grid",
                  pb: "0 !important",
                }}
              >
                <Typography
                  variant="body2"
                  color="primary"
                  sx={{ alignSelf: "end" }}
                >
                  {EnVKVText}
                </Typography>
              </CardContent>
            )}
          </Stack>

          <CardContent>
            <Stack direction="row" sx={{ justifyContent: "space-between" }}>
              <TextWrapper>
                <Typography
                  component="span"
                  variant="h5"
                  color="primary"
                  sx={{ fontWeight: "900" }}
                >
                  <FormattedNumber
                    style="currency"
                    currency="EUR"
                    value={vehicle.preis / vatFactor}
                    maximumFractionDigits={0}
                  />
                </Typography>
                {preisTitle && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    component="span"
                  >
                    ({preisTitle})
                  </Typography>
                )}
              </TextWrapper>

              {vehicle.rate && (
                <Box>
                  <Typography color="primary" component="span" variant="body2">
                    ab&nbsp;
                  </Typography>
                  <Typography
                    component="span"
                    variant="h6"
                    color="primary"
                    sx={{ fontWeight: "900" }}
                  >
                    <FormattedNumber
                      style="currency"
                      currency="EUR"
                      value={vehicle.rate}
                      maximumFractionDigits={0}
                    />
                    /m.
                  </Typography>
                </Box>
              )}
            </Stack>
          </CardContent>
        </Stack>
      </CardActionArea>
    </Card>
  );
};

const getVehicleClassName = (vehicle: TVehicle): string => {
  const { fzart, km, location, kraftstoff, aufbau } = vehicle;
  const _fzart = fzart?.id ? `fzart-${fzart.id}` : undefined;
  const _aufbau = aufbau?.id ? `aufbau-${aufbau.id}` : undefined;
  const _kraftstoff = kraftstoff?.id
    ? `kraftstoff-${kraftstoff.id}`
    : undefined;
  const _location = location?.id ? `location-${location.id}` : undefined;
  const _km =
    typeof km === "number"
      ? `km-${getLighter(km, [80000, 200000, 300000])}`
      : undefined;

  return [_fzart, _km, _location, _kraftstoff, _aufbau]
    .filter(Boolean)
    .join(" ");
};

const getLighter = (value: number, steps: number[]): string => {
  const result = steps.find((step: number) => value <= step);
  return result ? "lt-" + result : "gt-" + steps.pop();
};

interface TQualityIcon {
  score: number;
  sx: any;
  title?: string;
}

const QualityIcon: React.FC<TQualityIcon> = ({
  score = 1,
  sx = {},
  title = "",
}): JSX.Element => {
  const color: any = scaleLinear()
    .domain([0, 0.5, 1])
    /* @ts-expect-error */
    .range(["#770000", "#aaaa00", "#007700"]);
  const rotate: any = scaleLinear().domain([0, 1]).range([-180, 0]);

  const rgbValue: string = color(score);
  const rotation = rotate(score);
  return (
    <Avatar
      sx={{
        ...sx,
        backgroundColor: rgbValue,
      }}
      title={title + ", color:" + rgbValue}
    >
      <ThumbUpIcon
        sx={{
          transform: "rotate(" + rotation + "deg)",
        }}
      />
    </Avatar>
  );
};

export default Component;
